import { InMemoryLeadStore, RateLimiter, type LeadRecord, type LeadStore } from "@reno-estimator/leads";

// `window.RenoEstimatorConfig`'s type is declared once, in
// estimatorClient.ts (RenoEstimatorConfig), and reused here - same
// pattern analyticsClient.ts and PrivacyPolicyLink.tsx already use.
function readHostConfig() {
  return typeof window !== "undefined" ? window.RenoEstimatorConfig : undefined;
}

/**
 * Phase 23 fix (Full Application Red-Team Audit, Finding 1 - Critical):
 * this used to be the *only* implementation ever wired up, meaning every
 * lead - on the standalone app and, critically, on any real WordPress
 * deployment - was saved to a plain in-memory Map that died with the
 * browser tab, while the UI told the homeowner their request was
 * received. `HttpLeadStore` is the real backend for a WordPress
 * deployment: it POSTs the built `LeadRecord` to this plugin's own
 * `/leads` REST route (packages/wordpress/php/includes/class-leads-rest.php),
 * which persists it to a real database table and emails the site's admin
 * address. `getLeadStore()` picks it automatically whenever
 * `RenoEstimatorConfig.restUrl` is present (the same signal
 * estimatorClient.ts already uses to detect a WordPress host), exactly as
 * this file's own doc comment anticipated: "swapping this for a
 * REST-backed LeadStore later would not require any component in this
 * app to change." `InMemoryLeadStore` remains the standalone/dev/test
 * fallback - see `isLeadDeliveryConfigured()` below, which the UI uses to
 * stop claiming a real follow-up will happen when there's nowhere for
 * this lead to actually go.
 */
export class HttpLeadStore implements LeadStore {
  constructor(private readonly restUrl: string, private readonly nonce?: string) {}

  async save(lead: LeadRecord): Promise<void> {
    const response = await fetch(`${this.restUrl.replace(/\/$/, "")}/leads`, {
      method: "POST",
      credentials: "omit",
      headers: {
        "Content-Type": "application/json",
        ...(this.nonce ? { "X-WP-Nonce": this.nonce } : {})
      },
      body: JSON.stringify(lead)
    });
    if (!response.ok) {
      throw new Error(`Lead submission failed with status ${response.status}`);
    }
  }

  // Reading/listing/deleting leads is an administrative operation this
  // public-facing browser client has no business performing (and no
  // capability-gated route exists for it) - findRecentDuplicate() in
  // submit-lead.ts calls list() as part of the normal submission flow, so
  // this can't simply throw; it returns empty/no-op so client-side
  // duplicate detection degrades to "no known duplicates" rather than
  // breaking submission entirely. Real duplicate protection for the
  // WordPress backend is still open - see docs/unresolved-decisions.md.
  async getById(): Promise<LeadRecord | undefined> {
    return undefined;
  }

  async list(): Promise<LeadRecord[]> {
    return [];
  }

  async delete(): Promise<void> {
    // No-op: see the class doc comment above.
  }
}

let cachedStore: LeadStore | null = null;

export function getLeadStore(): LeadStore {
  if (!cachedStore) {
    const config = readHostConfig();
    cachedStore = config?.restUrl ? new HttpLeadStore(config.restUrl, config.wpRestNonce) : new InMemoryLeadStore();
  }
  return cachedStore;
}

/**
 * Whether a submission through `getLeadStore()` will actually reach
 * anyone. False in standalone/dev/test builds (no `RenoEstimatorConfig`,
 * or no `restUrl` configured) - the UI uses this to avoid promising a
 * follow-up that can't happen. See LeadCaptureForm.tsx.
 */
export function isLeadDeliveryConfigured(): boolean {
  return Boolean(readHostConfig()?.restUrl);
}

/**
 * The one place this app wires up lead-submission rate limiting (Phase
 * 20). A single shared instance so the limit is actually enforced across
 * submissions, not reset per form render - see
 * `@reno-estimator/leads`'s `submit-lead.ts` for why this is caller-
 * supplied rather than built into `submitLead` itself. 5 submissions per
 * 10 minutes per contact is a deliberately generous ceiling meant to stop
 * scripted abuse, not to get in a real homeowner's way.
 */
let cachedRateLimiter: RateLimiter | null = null;

export function getLeadRateLimiter(): RateLimiter {
  if (!cachedRateLimiter) {
    cachedRateLimiter = new RateLimiter({ maxRequests: 5, windowMs: 10 * 60 * 1000 });
  }
  return cachedRateLimiter;
}
